import { useMemo } from 'react';
import { SurveyAuditDTO } from '../dtos';
import { SurveyAuditTableData } from '../types';
import {
  useSurveyDisplayTitle,
  useTenantDisplayName,
  useSubjectDisplayName,
  useDomainDisplayName,
  useUserDisplayName,
  useSurveyInstanceDisplayTitle,
} from './useEntityDisplay';

export interface SurveyAuditDisplayData {
  surveyTitle: string;
  surveyTitleLoading: boolean;
  tenantName: string;
  tenantNameLoading: boolean;
  subjectName: string;
  subjectNameLoading: boolean;
  domainName: string;
  domainNameLoading: boolean;
  auditedByName: string;
  auditedByNameLoading: boolean;
  instanceTitle: string;
  instanceTitleLoading: boolean;
  isAnyLoading: boolean;
  hasAllData: boolean;
}

/**
 * Comprehensive hook that aggregates all display names for a survey audit
 * This provides user-friendly names instead of IDs for all entities
 */
export const useSurveyAuditDisplayData = (
  audit: SurveyAuditTableData | SurveyAuditDTO | undefined
): SurveyAuditDisplayData => {
  // Fetch all entity display names
  const { displayTitle: surveyTitle, isLoading: surveyTitleLoading } = useSurveyDisplayTitle(
    audit?.surveyId
  );
  const { displayName: tenantName, isLoading: tenantNameLoading } = useTenantDisplayName(
    audit?.tenantId
  );
  const { displayName: subjectName, isLoading: subjectNameLoading } = useSubjectDisplayName(
    audit?.tenantId,
    audit?.subjectId
  );
  const { displayName: domainName, isLoading: domainNameLoading } = useDomainDisplayName(
    audit?.tenantId,
    audit?.domainId
  );
  const { displayName: auditedByName, isLoading: auditedByNameLoading } = useUserDisplayName(
    audit?.auditedBy
  );
  const { displayTitle: instanceTitle, isLoading: instanceTitleLoading } =
    useSurveyInstanceDisplayTitle(audit?.surveyId, audit?.surveyInstanceId);

  const displayData: SurveyAuditDisplayData = useMemo(() => {
    const isAnyLoading =
      surveyTitleLoading ||
      tenantNameLoading ||
      subjectNameLoading ||
      domainNameLoading ||
      auditedByNameLoading ||
      instanceTitleLoading;

    // Check if we have actual names (not truncated IDs) for critical entities
    const hasActualSurveyTitle = surveyTitle && !surveyTitle.startsWith('Survey ');
    const hasActualAuditedByName =
      auditedByName && !auditedByName.startsWith('Unknown User') && !auditedByName.includes('...');

    return {
      surveyTitle,
      surveyTitleLoading,
      tenantName,
      tenantNameLoading,
      subjectName,
      subjectNameLoading,
      domainName,
      domainNameLoading,
      auditedByName,
      auditedByNameLoading,
      instanceTitle,
      instanceTitleLoading,
      isAnyLoading,
      hasAllData: Boolean(hasActualSurveyTitle && hasActualAuditedByName),
    };
  }, [
    surveyTitle,
    surveyTitleLoading,
    tenantName,
    tenantNameLoading,
    subjectName,
    subjectNameLoading,
    domainName,
    domainNameLoading,
    auditedByName,
    auditedByNameLoading,
    instanceTitle,
    instanceTitleLoading,
  ]);

  return displayData;
};

// Note: For multiple audits, create individual components that each call useSurveyAuditDisplayData
// to avoid violating the rules of hooks by calling hooks in loops
